import pako from "pako";
import Papa from "papaparse";
import * as R from "ramda";
import { base64ToUint8Array } from "@blisspointmedia/bpm-types/dist/binaryUtils";
import { reduxStore } from "../redux";
import * as UserRedux from "../redux/user";
import { download } from "../utils/download-utils";
import { artificialWait } from "./async";
import {
  getAccessToken,
  NotSignedInError,
  redirectToLogin,
  RefreshUnavailableError,
} from "./auth-client";
import { Mixpanel, MxE } from "./mixpanelWrapper";

const {
  REACT_APP_LAMBDA_HOST,
  REACT_APP_DOCKER_LAMBDA_HOST,
  REACT_APP_LOCAL_PYTHON_HOST,
} = process.env;

export const S3_POLLING_PERIOD = 1000;
export const S3_POLLING_TIMEOUT = 5 * 60;
export const COMET_POLLING_TIMEOUT = 16 * 60;
export const COMET_LOADING_BUCKET = "bpm-comet-loading";

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error
export class FetchError extends Error {
  constructor(message: string, public status = 500) {
    super(message);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FetchError);
    }

    this.name = "FetchError";
  }
}

/** A 401 whose recovery refresh couldn't be attempted because the sign-in service didn't answer.
 * Still a plain 401 FetchError to every caller; boot uses the subclass to tell an outage from a
 * rejected session, since only the latter may send the user to sign in. */
export class RefreshUnavailableFetchError extends FetchError {
  constructor(message: string, status = 401) {
    super(message, status);
    this.name = "RefreshUnavailableFetchError";
  }
}

export const getCleanEmail = (email: string): string | null => {
  // remove all whitespace
  email = email.replace(/\s/g, "");
  // https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
  let match = email.match(
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
  );
  return match && match[0];
};

export const awaitText = (response: Response): Promise<string> => response.text();

export const awaitJSON = async <T = any>(response: Response): Promise<T> => {
  let text = await response.text();
  try {
    let json = JSON.parse(text);
    return json;
  } catch (e) {
    let msg = `Failed to parse response as JSON. Parsed as text:\n${text}`;
    console.error(msg);
    throw new Error(msg);
  }
};

export const awaitGzippedJSON = async <T = any>(response: Response): Promise<T> => {
  const responseText = await response.text();
  let base64: string;
  try {
    base64 = JSON.parse(responseText);
  } catch {
    base64 = responseText;
  }
  const uncompressed = pako.ungzip(base64ToUint8Array(base64), { to: "string" });
  return JSON.parse(uncompressed);
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

// Bucket a rejected fetch() into a low-cardinality type so the telemetry stays queryable.
// A rejected fetch() is always transport-level (network/CORS/abort/timeout), never an HTTP status.
const classifyFetchError = (error: unknown): string => {
  const msg = getErrorMessage(error).toLowerCase();
  if (msg.includes("abort")) {
    return "aborted";
  }
  if (msg.includes("timeout") || msg.includes("timed out")) {
    return "timeout";
  }
  if (msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("network")) {
    return "network";
  }
  return "other";
};

// Snapshot of the client's transport state at failure time. Lets us split genuine
// offline/backgrounded give-ups (expected, client-side) from online+visible failures
// (which point at a server or CORS-masked error worth backend attention).
const getFailureContext = (): { online: boolean; visibility: string; connection?: string } => {
  const nav = navigator as Navigator & { connection?: { effectiveType?: string } };
  return {
    online: navigator.onLine,
    visibility: document.visibilityState,
    connection: nav.connection?.effectiveType,
  };
};

const isTabHidden = (): boolean => document.visibilityState === "hidden";

// How long to wait for a hidden tab to come back to the foreground before giving up on it and
// letting the caller proceed anyway. Bounded so a call that's part of a larger sequence (e.g. a
// batch job awaiting each item in turn) can't stall indefinitely on one backgrounded tab that
// never returns.
const VISIBILITY_WAIT_TIMEOUT_MS = 2 * 60 * 1000;

// BpmLambdaFetch's give-up path (its only caller) reaches this once every scheduled retry has
// failed and the tab was hidden at some point along the way. Resolves immediately if the tab is
// already visible; otherwise waits for it to come back to the foreground — or for
// VISIBILITY_WAIT_TIMEOUT_MS to elapse, whichever comes first — before giving the caller one
// last attempt. Always resolves (never rejects), so that attempt runs and fails or succeeds
// normally rather than needing separate timeout-handling.
const waitForVisible = (): Promise<void> => {
  if (!isTabHidden()) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const cleanup = () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearTimeout(timeoutId);
    };
    const onVisibilityChange = () => {
      if (!isTabHidden()) {
        cleanup();
        resolve();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, VISIBILITY_WAIT_TIMEOUT_MS);
  });
};

const getFetchErrorMessage = (error: unknown, path: string, params: any) => {
  let resParams = {
    message: error instanceof Error ? error.message : error,
    path: path || "N/A",
    params: params || "N/A",
  };
  let msg = JSON.stringify(resParams);
  return msg;
};

// TODO: We should figure out a way to pass along the status code. If the error is a 409 or something, for instance, I
// might want to do something special.
export const awaitStatus = async (response: Response): Promise<boolean> => {
  // If there is no status, that means the fetch was canceled, and we shouldn't do anything
  if (!response.status) {
    return true;
  } else if (response.status >= 200 && response.status < 300) {
    // 300s might be needed as well (304 not modified)
    return true;
  } else if (`${response.status}` === "404") {
    throw new FetchError("404", response.status);
  } else if (`${response.status}` === "503") {
    throw new FetchError("Service Unavailable", response.status);
  } else {
    try {
      let res = await response.json();
      let { message, path, queryStringParameters } = res;
      if (message) {
        throw new FetchError(getFetchErrorMessage(message, path, queryStringParameters));
      }
      throw new Error();
    } catch (e) {
      console.error(e);
      throw new FetchError(getErrorMessage(e), response.status);
    }
  }
};

type ValidParam = string | number | boolean;

export type HttpMethod =
  | "GET"
  | "get"
  | "POST"
  | "post"
  | "PUT"
  | "put"
  | "DELETE"
  | "delete"
  | "PATCH"
  | "patch";
interface LambdaFetchOptions<T = Record<string, ValidParam | ValidParam[]>> {
  method: HttpMethod;
  authenticate?: boolean;
  retries?: number;
  headers: Record<string, any>;
  params?: T;
  body?: T;
  mode?: "cors" | "no-cors" | "same-origin";
}

const DEFAULT_RETRIES = 2;

const LOGGED_OUT_MESSAGE = "You've been logged out. Please sign out and log back in.";

/** The session is gone. The redirect to login has already been attempted here, once; `redirected`
 * says whether it navigated (false when this page is the sign-in page's own '/'), so callers
 * decide what to show without navigating again. */
export class SignedOutError extends NotSignedInError {
  redirected: boolean;

  constructor(message: string, redirected: boolean) {
    super(message);
    this.redirected = redirected;
  }
}

// A signed-out session sends the user to login; a sign-in service that can't answer says so
// instead, since the session may well still be good.
const handleTokenError = (e: unknown): Error => {
  if (e instanceof NotSignedInError) {
    return new SignedOutError(LOGGED_OUT_MESSAGE, redirectToLogin());
  }
  if (e instanceof RefreshUnavailableError) {
    return new Error("Couldn't reach the sign-in service. Please try again in a moment.");
  }
  return new Error(LOGGED_OUT_MESSAGE);
};

// Builds the Authorization header on `headers` from the current access token. Used both before
// the retry loop and again before the bonus attempt (whose token may have gone stale during an
// arbitrarily long visibility wait), so the two call sites can't drift out of sync.
const setAuthHeader = async (headers: Record<string, any>): Promise<void> => {
  try {
    headers.Authorization = `bearer ${await getAccessToken()}`;
  } catch (e) {
    console.log("Could not get an access token", e);
    throw handleTokenError(e);
  }
};

// After a 401 on an authenticated call: fetches a freshly refreshed token and retries once. A
// second 401 is returned as is, so a rejected token can't loop. Returns the original response
// when there is nothing better to retry with (the sign-in service can't answer, the refresh
// produced the same token, or the retry itself never completed); `refreshUnavailable` is set when
// the cause is the sign-in service not answering.
const retryAfterUnauthorized = async (
  unauthorized: Response,
  headers: Record<string, any>,
  send: () => Promise<Response>
): Promise<{ response: Response; refreshUnavailable: boolean }> => {
  const rejectedAuthorization = headers.Authorization;
  try {
    headers.Authorization = `bearer ${await getAccessToken({ forceRefresh: true })}`;
  } catch (e) {
    if (e instanceof NotSignedInError) {
      throw handleTokenError(e);
    }
    return { response: unauthorized, refreshUnavailable: e instanceof RefreshUnavailableError };
  }
  if (headers.Authorization === rejectedAuthorization) {
    return { response: unauthorized, refreshUnavailable: false };
  }
  try {
    return { response: await send(), refreshUnavailable: false };
  } catch (e) {
    console.error(`Retry after refreshing the access token failed. Error: ${getErrorMessage(e)}`);
    return { response: unauthorized, refreshUnavailable: false };
  }
};

export const BpmLambdaFetch = async <T>(
  service: string,
  useDockerLambdas = false,
  useLocalPython = false,
  path = "/",
  passedOptions: Partial<LambdaFetchOptions<T>> = {},
  useGlobalHost = false
): Promise<Response> => {
  let options = R.mergeDeepRight<LambdaFetchOptions<T>, Partial<LambdaFetchOptions<T>>>(
    {
      method: "GET",
      authenticate: true,
      retries: DEFAULT_RETRIES,
      headers: {},
    },
    passedOptions
  ) as LambdaFetchOptions;

  let headers = {
    ...(R.prop("headers", options) || {}),
  };

  const authenticated = Boolean(options.authenticate);
  if (authenticated) {
    await setAuthHeader(headers);
  }
  options.headers = headers;
  delete options.authenticate;

  let paramStr = "";

  if (options.params) {
    paramStr = "?";
    let keys = R.keys(options.params);
    for (let key of keys) {
      // If we pass anything - false, null, 0 - APIGateway / Lambda will always interpret it as truthy smh. Only do nils
      // or false, though, since an empty string or a 0 might actually be valid.
      if (R.isNil(options.params[key]) || options.params[key] === false) {
        continue;
      }
      if (paramStr !== "?") {
        paramStr += "&";
      }

      // if we are sending multiple QS-params of the same name, it's passed in as an array
      if (Array.isArray(options.params[key])) {
        for (let val of options.params[key] as ValidParam[]) {
          if (paramStr !== "?") {
            paramStr += "&";
          }

          paramStr = `${paramStr}${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
        }
      } else {
        paramStr = `${paramStr}${encodeURIComponent(key)}=${encodeURIComponent(
          options.params[key] as ValidParam
        )}`;
      }
    }
  }
  let body: string | undefined;
  if (options.body) {
    if (typeof options.body == "object") {
      body = JSON.stringify(options.body);
    } else {
      ({ body } = options);
    }
  }

  let initTries = options.retries || DEFAULT_RETRIES;
  let tries = initTries + 1;
  delete options.retries;
  let res: Response | undefined;

  if (window.location.hostname === "localhost") {
    useDockerLambdas = false;
  }

  let baseUrl: string;
  if (useLocalPython) {
    baseUrl = REACT_APP_LOCAL_PYTHON_HOST || REACT_APP_LAMBDA_HOST || "";
  } else {
    baseUrl = useDockerLambdas
      ? REACT_APP_DOCKER_LAMBDA_HOST || REACT_APP_LAMBDA_HOST || ""
      : REACT_APP_LAMBDA_HOST || "";
  }

  // Services verified safe in every prod region (eu-west-2 included) route through the all-regions
  // api-global domain instead of the US-only api-prod domain. The two are sibling hostnames, so we
  // swap api-prod -> api-global on the resolved host. In staging/dev the host isn't api-prod, so this
  // is a no-op and the service falls back to its normal host (api-global exists in prod only).
  if (useGlobalHost) {
    baseUrl = baseUrl.replace("api-prod.", "api-global.");
  }

  let url = `${baseUrl}/${service}${path}${paramStr}`;
  // Route + method only for telemetry — never the query string (avoids PII and keeps cardinality low).
  const endpoint = `${service}${path}`;
  let lastError: unknown;
  // Whether the tab was hidden during any failed attempt. Gates the bonus attempt below (see
  // that block for why a live visibility check there isn't enough) and is also surfaced in the
  // give-up telemetry so the visibility-throttling hypothesis can be measured against real
  // traffic, not just inferred from the visibility snapshot at the moment of final failure.
  let pausedForVisibility = false;
  do {
    try {
      res = await fetch(url, {
        ...options,
        body,
      });
      break;
    } catch (e) {
      lastError = e;
      console.error(`Failed to fetch. Error: ${getErrorMessage(e)}`);
      console.error("Tries: ", --tries);
      if (isTabHidden()) {
        pausedForVisibility = true;
      }
      await artificialWait(5000);
    }
  } while (tries > 0);

  // Every attempt above still fires on its normal cadence regardless of visibility, so a page
  // that's meant to keep loading/polling in the background keeps doing so. But if the tab was
  // hidden at any point during those attempts, background-tab throttling was almost certainly a
  // factor (see lambda_fetch_failed telemetry). Give it one more try — bounded by
  // VISIBILITY_WAIT_TIMEOUT_MS above — once the tab is genuinely back in the foreground (or that
  // timeout elapses) before surfacing a failure nobody has even seen yet, rather than leaving a
  // stale error for whenever the user returns — especially since nothing else retries on our
  // behalf.
  //
  // Gated on pausedForVisibility (was the tab hidden at some point) rather than a live
  // isTabHidden() check here: a live check would miss the case where the user already returned
  // during the retry loop's last artificialWait(5000) above, denying the bonus attempt exactly
  // when the user has genuinely come back — the one case this whole mechanism exists for.
  const bonusAttempt = !res && pausedForVisibility;
  if (bonusAttempt) {
    await waitForVisible();
    // The Authorization header above was captured once, before any of this waiting — refresh it
    // so an arbitrarily long wait can't turn a should-have-recovered request into an avoidable
    // 401 from a rotated/expired token.
    if (headers.Authorization) {
      await setAuthHeader(headers);
    }
    try {
      res = await fetch(url, { ...options, body });
    } catch (e) {
      lastError = e;
    }
  }

  let refreshUnavailable = false;
  if (res && res.status === 401 && authenticated) {
    ({ response: res, refreshUnavailable } = await retryAfterUnauthorized(res, headers, () =>
      fetch(url, { ...options, body })
    ));
  }

  if (res) {
    // Record server/transient HTTP failures (5xx, 429, 408) so the visible-status side of the
    // distribution shows up next to the transport-level give-ups below. awaitStatus throws on non-2xx.
    if (!res.ok && (res.status >= 500 || res.status === 429 || res.status === 408)) {
      Mixpanel.track(MxE.LAMBDA_FETCH_FAILED, {
        service,
        endpoint,
        method: options.method,
        error_type: "http_status",
        status: res.status,
        ...getFailureContext(),
      });
    }
    try {
      await awaitStatus(res);
    } catch (e) {
      if (refreshUnavailable && e instanceof FetchError) {
        throw new RefreshUnavailableFetchError(e.message, e.status);
      }
      throw e;
    }
    return res;
  }
  let millisAlive = Date.now() - (UserRedux.startTimeSelector(reduxStore.getState()) as number);
  let hoursAlive = millisAlive / 3600000;
  console.error(
    `Could not complete request after ${initTries} retries. Request: ${options.method} ${url}. Hours Alive: ${hoursAlive}`
  );
  // Transport-level give-up — the "Could not complete request." case: every fetch() attempt
  // rejected (network loss, CORS failure incl. CORS-less 5xx/504, or abort), so there is no status.
  Mixpanel.track(MxE.LAMBDA_FETCH_FAILED, {
    service,
    endpoint,
    method: options.method,
    error_type: classifyFetchError(lastError),
    error_message: getErrorMessage(lastError),
    attempts: initTries + 1 + (bonusAttempt ? 1 : 0),
    hours_alive: Math.round(hoursAlive * 100) / 100,
    paused_for_visibility: pausedForVisibility,
    ...getFailureContext(),
  });
  throw new Error(getFetchErrorMessage("Could not complete request.", url, options.params));
};

export const GetS3SignedUrl = async (path: string): Promise<string> => {
  let res = await AdminLambdaFetch(`/signedUrl?path=${path}`);
  res = await awaitJSON(res);
  return R.prop("url", res);
};

// Follows url redirect through JSON payload `url` property
export const S3SignedUrlFetch = async (path: string, options: object = {}): Promise<any> => {
  const signedURL = await GetS3SignedUrl(path);
  let res = await fetch(signedURL, options);
  return res;
};

export const GetS3SignedUrl2 = async (
  bucket: string,
  key: string,
  contentType?: string
): Promise<string> => {
  let params: Record<string, string> = {
    bucket,
    key,
  };

  if (contentType) {
    params.contentType = contentType;
  }
  let res = await ToolsLambdaFetch("/s3_signed_url", {
    params,
  });
  res = await awaitJSON(res);
  return R.prop("url", res);
};

export const S3SignedUrlFetch2 = async (
  bucket: string,
  key: string,
  contentType?: string
): Promise<Response> => {
  const signedURL = await GetS3SignedUrl2(bucket, key, contentType);
  let res = await fetch(signedURL);
  return res;
};

// Downloads a `<bucket>/<key>` cache CSV (as returned by the lambdas'
// uploadCsvToCache) and parses it. Defaults to header rows with empty lines
// skipped; pass parseConfig to override or extend (e.g. transformHeader).
export const fetchAndParseCachedCsv = async <T>(
  cacheKey: string,
  parseConfig: Omit<Papa.ParseConfig<T>, "download"> = {}
): Promise<T[]> => {
  const res = await S3SignedUrlFetch(cacheKey);
  if (!res.ok) {
    throw new Error(`Failed to download cached CSV ${cacheKey} (${res.status}).`);
  }
  const text = await res.text();
  const { data } = Papa.parse<T>(text, {
    header: true,
    skipEmptyLines: true,
    ...parseConfig,
  });
  return data || [];
};

interface PotentialUnfoundCheckMaker<T> {
  (): Promise<T | false>;
}

export const makePotentiallyUnfoundCheck = async (
  fetcher: () => Promise<Response>
): Promise<Response | false> => {
  try {
    let res = await fetcher();
    if (res.status === 200) {
      return res;
    }
    if (res.status !== 404) {
      let errorText: string;
      try {
        errorText = await awaitText(res);
      } catch (e) {
        errorText = res.statusText;
      }
      throw new Error(errorText);
    }
    return false;
  } catch (e) {
    return false;
  }
};

type ErrorCheckMaker = PotentialUnfoundCheckMaker<string>;

export const makePollingErrorChecker = async (
  fetcher: () => Promise<Response>
): Promise<string | false> => {
  let res = await makePotentiallyUnfoundCheck(fetcher);
  if (res !== false) {
    return await res.text();
  }
  return false;
};

interface PollOptions<T> {
  fetchMaker: PotentialUnfoundCheckMaker<T>;
  errorCheckMaker?: ErrorCheckMaker;
  period?: number; // Milliseconds
  timeout?: number; // Seconds. If -1, then no timeout
  maxAttempts?: number; // Maximum tries to fetch the resource
}

export const poll = async <T>({
  fetchMaker,
  errorCheckMaker,
  period = S3_POLLING_PERIOD,
  timeout = S3_POLLING_TIMEOUT,
  maxAttempts,
}: PollOptions<T>): Promise<T> => {
  let start = Date.now();
  let latest = Date.now();
  let attemptNumber = 0;
  while (timeout < 0 || (latest - start) / 1000 < timeout) {
    if (attemptNumber === maxAttempts) {
      throw new Error(`Attempted to fetch the maximum number of times: ${maxAttempts}`);
    }
    await artificialWait(period);
    try {
      let response = await fetchMaker();
      if (response !== false) {
        return response;
      }

      if (errorCheckMaker) {
        let errorMessage = await errorCheckMaker();
        if (errorMessage !== false) {
          throw Error(errorMessage);
        }
      }
    } catch (e) {
      throw new Error(getErrorMessage(e));
    }
    latest = Date.now();
    attemptNumber++;
  }
  throw new Error("Timeout");
};

interface PollS3Options {
  bucket: string;
  filename: string;
  mimeType: string;
  ext?: string;
  autoDownload?: boolean;
  overloadFilename?: string;
  noTimeout?: boolean;
  withErrors?: boolean;
  timeout?: number;
  maxAttempts?: number;
  period?: number;
}

export const pollS3 = async ({
  bucket,
  filename,
  mimeType,
  ext,
  autoDownload = true,
  maxAttempts = -1, // Default no maximum
  overloadFilename,
  noTimeout = false,
  withErrors = false,
  timeout = S3_POLLING_TIMEOUT,
  period = S3_POLLING_PERIOD,
}: PollS3Options): Promise<Blob> => {
  let errorCheckMaker: undefined | (() => Promise<string | false>);
  if (withErrors) {
    errorCheckMaker = () =>
      makePollingErrorChecker(() =>
        S3SignedUrlFetch2(bucket, `${filename}.error.txt`, "text/plain")
      );
  }
  let response = await poll({
    fetchMaker: () =>
      makePotentiallyUnfoundCheck(() =>
        S3SignedUrlFetch2(bucket, ext ? `${filename}.${ext}` : filename, mimeType)
      ),
    errorCheckMaker,
    period,
    timeout: noTimeout ? -1 : timeout,
    maxAttempts,
  });
  if (!response) {
    throw new Error(`Unable to download s3://${bucket}/${filename}${ext ? ext : ""}`);
  }
  let content = await response.blob();
  if (autoDownload) {
    download(content, overloadFilename ? overloadFilename : filename, mimeType);
  }
  return content;
};

interface S3PutOptions {
  bufferEncoding?: string;
  contentEncoding?: string;
  contentType?: string;
}
// TODO: if I wasn't lazy, I'd make a type for what I know this lambda returns
export const S3Put = (
  bucket: string,
  key: string,
  body: string,
  options?: S3PutOptions
): Promise<any> =>
  ToolsLambdaFetch("/s3", {
    method: "post",
    body: {
      body,
      bucket,
      key,
      ...options,
    },
  });

export const S3PutPng = (bucket: string, key: string, body: string): Promise<any> =>
  S3Put(bucket, key, body.replace(/^data:image\/png;base64,/, ""), {
    bufferEncoding: "base64",
    contentType: "image/png",
  });

export const S3PutMp4 = (bucket: string, key: string, body: string): Promise<any> =>
  S3Put(bucket, key, body.replace(/^data:video\/mp4;base64,/, ""), {
    bufferEncoding: "base64",
    contentType: "video/mp4",
  });

export const S3Delete = (bucket: string, key: string): Promise<any> =>
  ToolsLambdaFetch("/s3", {
    method: "delete",
    params: {
      bucket,
      key,
    },
  });

export interface S3ListParams {
  bucket: string;
  prefix: string;
  maxKeys?: number;
  contains?: string;
}

export const S3List = async (params: S3ListParams): Promise<string[]> => {
  const res = await ToolsLambdaFetch("/S3List", {
    params,
  });
  const data = await awaitJSON<{ keys: string[] }>(res);
  return data.keys ?? [];
};

type BpmLambdaFetcher = <T>(
  path: string,
  fetchOptions?: Partial<LambdaFetchOptions<T>> | undefined
) => Promise<Response>;

const makeFetcher = (
  service: string,
  useDockerLambdas = false,
  useLocalPython = false,
  useGlobalHost = false
): BpmLambdaFetcher => <T>(
  path: string,
  fetchOptions?: Partial<LambdaFetchOptions<T>>
): Promise<Response> => {
  return BpmLambdaFetch(service, useDockerLambdas, useLocalPython, path, fetchOptions, useGlobalHost);
};

export const ABCDLambdaFetch = makeFetcher("abcd", true);
export const ABCDPyLambdaFetch = makeFetcher("abcdpy", true, true);
export const AdminLambdaFetch = makeFetcher("admin", true);
export const AmazonLambdaFetch = makeFetcher("amazon", true);
export const AmcLambdaFetch = makeFetcher("amc", true);
export const AnomalyDetectionLambdaFetch = makeFetcher("anomalydetection", true);
export const BenchmarkingLambdaFetch = makeFetcher("benchmarking", true);
export const ClientLambdaFetch = makeFetcher("client");
export const ClientToolsLambdaFetch = makeFetcher("clienttools");
export const CommerceOpsLambdaFetch = makeFetcher("commerceops", true);
export const CopyTigerLambdaFetch = makeFetcher("copytiger", true);
export const CopyTigerPyLambdaFetch = makeFetcher("copytigerpy", true, true);
export const CreativeLambdaFetch = makeFetcher("creative", true);
export const CrossChannelLambdaFetch = makeFetcher("crosschannel", true);
export const DagLambdaFetch = makeFetcher("dag", true);
export const ExtremeReachLambdaFetch = makeFetcher("extremereach", true);
// filters is eu-west-2-safe (Postgres-only) → route it through api-global (all regions incl. eu-west-2).
export const FiltersLambdaFetch = makeFetcher("filters", true, false, true);
export const FlashtalkingLambdaFetch = makeFetcher("flashtalking", true);
export const HROASLambdaFetch = makeFetcher("hroas", true, false, true);
export const HROASPyLambdaFetch = makeFetcher("hroaspy", true, true);
export const JavaLambdaFetch = makeFetcher("java", true);
export const JobsLambdaFetch = makeFetcher("jobs", true);
export const LinearBuyingLambdaFetch = makeFetcher("linearbuying", true, false, true);
export const LinearLambdaFetch = makeFetcher("linear", true);
export const LinearOptimizationsLambdaFetch = makeFetcher("linearoptimizations", true, false, true);
export const LinearPerformanceLambdaFetch = makeFetcher("linearperformance", true);
export const MetaLambdaFetch = makeFetcher("meta", true);
export const MetricsLambdaFetch = makeFetcher("metrics", true);
export const MiscLambdaFetch = makeFetcher("misc", true);
export const MobiusSlidesLambdaFetch = makeFetcher("mobiusslides", true);
export const ScenarioPlanningLambdaFetch = makeFetcher("scenarioplanning", true, false, true);
export const ScenarioPlanningPyLambdaFetch = makeFetcher("scenarioplanningpy", true, true);
export const SegmentationMappingLambdaFetch = makeFetcher("segmentationmapping", true);
export const SheetsLambdaFetch = makeFetcher("sheets", true, false, true);
export const SingleChannelLambdaFetch = makeFetcher("singlechannel", true);
export const SiteMonitoringLambdaFetch = makeFetcher("sitemonitoring", true);
export const SlidesLambdaFetch = makeFetcher("slides", true);
export const StreamingBuyingLambdaFetch = makeFetcher("streamingbuying");
export const StreamingDeliveryLambdaFetch = makeFetcher("streaming-delivery", true);
export const StreamingLambdaFetch = makeFetcher("streaming", true);
export const StreamingNetworksLambdaFetch = makeFetcher("streaming-networks", true);
export const StreamingOptimizationsLambdaFetch = makeFetcher("streamingoptimizations", true);
export const StreamingPerformanceLambdaFetch = makeFetcher("streamingperformance", true);
export const StreamingUtilsLambdaFetch = makeFetcher("streamingutils", true, false, true);
export const StreamingV2LambdaFetch = makeFetcher("streamingv2", true);
export const ToolsLambdaFetch = makeFetcher("tools", true);
export const UsersLambdaFetch = makeFetcher("users", true, false, true);
export const WtoLambdaFetch = makeFetcher("wto", true, false, true);
export const YoutubePerformanceLambdaFetch = makeFetcher("youtubeperformance", true);
export const YoutubePlanningLambdaFetch = makeFetcher("youtubeplanning", true);

// Reserved field bpm-lambdas' cometInputWrapper looks for. A request carrying it is acked with
// {comet:true, uuid} and the real work runs in a self-invoked worker that writes `{uuid}.json`.
const COMET_TRIGGER_FIELD = "__cometTrigger";

interface CometSelfInvokeRoute {
  fetcher: BpmLambdaFetcher;
  path: string;
  method: "get" | "post";
}

// Handlers that run through their own cometInputWrapper route
// (bpm-lambdas #9142, #9143). Keyed by the old dispatcher lambdaName so existing callers keep working.
// A function rather than a const map because the fetchers are declared further down this file.
const getCometSelfInvokeRoute = (lambdaName: string): CometSelfInvokeRoute | undefined => {
  switch (lambdaName) {
    case "linear-getProcessedPostlogs":
      return { fetcher: LinearLambdaFetch, path: "/processed_post_logs", method: "get" };
    case "linearPerformance-getPageData":
      return { fetcher: LinearPerformanceLambdaFetch, path: "/", method: "get" };
    case "streamingPerformance-getPageData":
      return { fetcher: StreamingPerformanceLambdaFetch, path: "/", method: "get" };
    case "youtubePerformance-getPageData":
      return { fetcher: YoutubePerformanceLambdaFetch, path: "/", method: "get" };
    case "creative-getCreativeInsightsIsciData":
      return { fetcher: CreativeLambdaFetch, path: "/getCreativeInsightsIsciData", method: "get" };
    case "llm-sendPrompt":
      return { fetcher: MiscLambdaFetch, path: "/sendLLMPrompt", method: "post" };
    case "segmentationmapping-getRawCampaignData":
      return {
        fetcher: SegmentationMappingLambdaFetch,
        path: "/getRawCampaignData",
        method: "get",
      };
    case "segmentationmapping-getCampaignOptions":
      return {
        fetcher: SegmentationMappingLambdaFetch,
        path: "/getCampaignOptions",
        method: "get",
      };
    case "streamingpacing-getPacingDashboardInfo":
      return { fetcher: AdminLambdaFetch, path: "/pacing", method: "get" };
    case "metrics-updateMMMOfflineInputs":
      return { fetcher: MetricsLambdaFetch, path: "/updateMMMOfflineInputs", method: "post" };
    default:
      return undefined;
  }
};

/*
 * Triggers a handler composed with cometInputWrapper through its own route (same method as the
 * route's plain call), then polls `{uuid}.json` from the comet loading bucket.
 */
const fetchCometSelfInvoke = async <T, K>(
  { fetcher, path, method }: CometSelfInvokeRoute,
  lambdaArgs: T,
  noTimeout: boolean,
  period: number,
  timeout: number
): Promise<K> => {
  const triggerArgs = { ...lambdaArgs, [COMET_TRIGGER_FIELD]: true };
  const res =
    method === "get"
      ? await fetcher(path, { method, params: triggerArgs })
      : await fetcher(path, { method, body: triggerArgs });
  const text = await res.text();
  let ack: { comet?: boolean; uuid?: string } | undefined;
  try {
    ack = JSON.parse(text);
  } catch {
    ack = undefined;
  }
  if (!ack?.comet || typeof ack.uuid !== "string") {
    throw new Error(
      `Comet trigger to ${method.toUpperCase()} ${path} did not return a comet ack ({comet:true, ` +
        `uuid}); the bpm-lambdas change may not be deployed yet. Response: ${text.slice(0, 200)}`
    );
  }
  const content = await pollS3({
    autoDownload: false,
    bucket: COMET_LOADING_BUCKET,
    filename: `${ack.uuid}.json`,
    mimeType: "text/plain",
    period,
    noTimeout,
    timeout,
  });
  const lambdaResult = JSON.parse(await content.text());
  if (lambdaResult && typeof lambdaResult === "object") {
    if ("cometErrorMessage" in lambdaResult) {
      throw new Error(lambdaResult.cometErrorMessage);
    }
    // The worker appends a progress sentinel to the final payload; consumers never want it.
    delete lambdaResult.cometProgress;
  }
  return lambdaResult as K;
};

/*
 * Hits the handler's Comet self-invoke route (looked up by its old lambdaName) and parses the response,
 * requires error handling
 */
export const fetchAndParseCometLambda = <T, K>(
  lambdaName: string,
  lambdaArgs: T,
  noTimeout = false,
  period = S3_POLLING_PERIOD,
  timeout = COMET_POLLING_TIMEOUT
): Promise<K> => {
  const selfInvokeRoute = getCometSelfInvokeRoute(lambdaName);
  if (selfInvokeRoute) {
    return fetchCometSelfInvoke<T, K>(selfInvokeRoute, lambdaArgs, noTimeout, period, timeout);
  }
  return Promise.reject(
    new Error(
      `No Comet self-invoke route is registered for "${lambdaName}". The central dispatcher is gone; add the route to getCometSelfInvokeRoute.`
    )
  );
};

interface CometOrStandardFetchParams<T> {
  service: BpmLambdaFetcher;
  standardEndpoint: string;
  cometLambdaName: string;
  input: T;
  period?: number;
  additionalFlag?: boolean;
  method?: HttpMethod;
  noTimeout?: boolean;
}

/*
 * Basic function to hit comet or standard function based on ENV and flag
 */
export const fetchCometLambda = async <T, K>({
  service,
  standardEndpoint,
  cometLambdaName,
  input,
  period = 3000,
  additionalFlag = true,
  method = "get",
  noTimeout = false,
}: CometOrStandardFetchParams<T>): Promise<K> => {
  if (additionalFlag && process.env.NODE_ENV === "production") {
    return await fetchAndParseCometLambda<T, K>(cometLambdaName, input, noTimeout, period);
  } else {
    const lambdaFetchOptions: Partial<LambdaFetchOptions<T>> = {
      method,
    };
    if (method === "get") {
      lambdaFetchOptions.params = input;
    } else {
      lambdaFetchOptions.body = input;
    }

    const res = await service<T>(standardEndpoint, lambdaFetchOptions);
    return await awaitJSON<K>(res);
  }
};

export interface FallbackFetchParams<T> {
  service: BpmLambdaFetcher;
  standardEndpoint: string;
  cometLambdaName: string;
  input: T;
  period?: number;
  method?: HttpMethod;
  noTimeout?: boolean;
  timeoutMs?: number;
  ttlMs?: number;
}

const COMET_FALLBACK_LS_PREFIX = "comet_fallback_v1";
const DEFAULT_COMET_TIMEOUT_MS = 25_000;
const DEFAULT_COMET_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const buildCometFallbackKey = <T>(lambdaName: string, input: T): string => {
  const stable = JSON.stringify(
    Object.fromEntries(
      Object.entries(input as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    )
  );
  return `${COMET_FALLBACK_LS_PREFIX}:${lambdaName}:${stable}`;
};

const isCometFallbackMarked = (key: string): boolean => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return false;
    }
    const expiry = parseInt(raw, 10);
    if (isNaN(expiry) || Date.now() > expiry) {
      localStorage.removeItem(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const markCometFallback = (key: string, ttlMs: number): void => {
  try {
    localStorage.setItem(key, String(Date.now() + ttlMs));
  } catch {
    // storage quota exceeded or unavailable
  }
};

/*
 * Try the direct Lambda first; fall back to comet on timeout or 504.
 * Saves a localStorage marker so future calls with the same args skip straight to comet.
 * In non-production, always hits the Lambda directly (no localStorage, no comet).
 */
export const fetchWithCometFallback = async <T, K>({
  service,
  standardEndpoint,
  cometLambdaName,
  input,
  period = 3000,
  method = "get",
  noTimeout = false,
  timeoutMs = DEFAULT_COMET_TIMEOUT_MS,
  ttlMs = DEFAULT_COMET_TTL_MS,
}: FallbackFetchParams<T>): Promise<K> => {
  const fetchOpts: Partial<LambdaFetchOptions<T>> = { method };
  if (method === "get") {
    fetchOpts.params = input;
  } else {
    fetchOpts.body = input;
  }

  if (process.env.NODE_ENV !== "production") {
    const res = await service<T>(standardEndpoint, fetchOpts);
    return awaitJSON<K>(res);
  }

  const lsKey = buildCometFallbackKey(cometLambdaName, input);
  if (isCometFallbackMarked(lsKey)) {
    return fetchAndParseCometLambda<T, K>(cometLambdaName, input, noTimeout, period);
  }

  const timeoutRace = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("comet_fallback_timeout")), timeoutMs)
  );

  try {
    return await Promise.race([
      service<T>(standardEndpoint, fetchOpts).then(res => awaitJSON<K>(res)),
      timeoutRace,
    ]);
  } catch (err) {
    const isSlowOrGone =
      (err instanceof Error && err.message === "comet_fallback_timeout") ||
      (err instanceof FetchError && err.status === 504);
    if (isSlowOrGone) {
      markCometFallback(lsKey, ttlMs);
      return fetchAndParseCometLambda<T, K>(cometLambdaName, input, noTimeout, period);
    }
    throw err;
  }
};
